import Content from '@/services/Content';

/** 64 MB — suitable for dashboard feed uploads (AWS minimum part size is 5 MB). */
export const UPLOAD_CHUNK_SIZE = 64 * 1024 * 1024;

/** 1 GB — used by the video editor for very large source files. */
export const UPLOAD_CHUNK_SIZE_LARGE = 1024 * 1024 * 1024;

/**
 * Uppy AwsS3Multipart callbacks. Sends upload_from so the API picks the S3 bucket.
 *
 * @param {string} [uploadFrom='editor'] 'videos' | 'shorts' | 'editor'
 */
export function createUppyS3MultipartHandlers(uploadFrom = 'editor') {
    const resolveUploadFrom = (file) => file?.meta?.uploadFrom || uploadFrom;

    const withUploadFrom = (file, payload) => ({
        ...payload,
        upload_from: resolveUploadFrom(file),
    });

    return {
        async createMultipartUpload(file) {
            file.meta = { ...(file.meta || {}), uploadFrom };

            const { data } = await Content.createMultipartUpload(withUploadFrom(file, {
                filename: file.name,
                content_type: file.type || 'video/mp4',
            }));

            return { uploadId: data.upload_id, key: data.key };
        },

        async signPart(file, partData) {
            const { data } = await Content.signPart(withUploadFrom(file, {
                upload_id: partData.uploadId,
                key: partData.key,
                part_number: partData.partNumber,
            }));

            return { url: data.url };
        },

        async completeMultipartUpload(file, { uploadId, key, parts }) {
            const { data } = await Content.completeMultipartUpload(withUploadFrom(file, {
                upload_id: uploadId,
                key,
                parts: parts.map((p) => ({
                    PartNumber: p.PartNumber ?? p.partNumber,
                    ETag: p.ETag ?? p.eTag ?? p.etag ?? null,
                })).filter((p) => p.PartNumber != null),
            }));

            return { location: data.location };
        },

        async abortMultipartUpload(file, partData) {
            await Content.abortMultipartUpload(withUploadFrom(file, {
                upload_id: partData.uploadId,
                key: partData.key,
            }));
        },
    };
}

/**
 * Upload a single File via Uppy + S3 multipart. Resolves with the S3 object key.
 *
 * @param {import('@uppy/core').Uppy} uppy
 * @param {File} file
 * @returns {Promise<string>}
 */
export function uploadFileWithUppy(uppy, file) {
    return new Promise((resolve, reject) => {
        uppy.getFiles().forEach((f) => uppy.removeFile(f.id));

        const onComplete = (result) => {
            cleanup();
            if (result.successful.length) {
                resolve(result.successful[0].s3Multipart.key);
                return;
            }
            reject(new Error('Upload failed'));
        };

        const onError = () => {
            cleanup();
            reject(new Error('Upload failed'));
        };

        const cleanup = () => {
            uppy.off('complete', onComplete);
            uppy.off('upload-error', onError);
        };

        uppy.on('complete', onComplete);
        uppy.on('upload-error', onError);

        uppy.addFile({
            name: file.name,
            type: file.type || 'video/mp4',
            data: file,
        });
        uppy.upload();
    });
}
